import { useAuth0 } from '@auth0/auth0-react';
import { Typography } from '@mui/material';
import { FC, useEffect, useRef, useState } from 'react';
import { useCurrentUser } from 'api/hooks/useCurrentUser';
import Dialog from 'components/Dialog';
import { useLanguage } from 'context/AppContext';
import { useAuthorization } from 'hooks/useAuth';
import { useSessionTimeoutSettings } from 'hooks/useSessionTimeoutSettings';
import { formatRemaining } from 'utils/helpers/time.helpers';

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'wheel', 'scroll'];

const MS_PER_MIN = 60_000;

const SessionTimeoutWarning: FC = () => {
  const lang = useLanguage()[0];
  const { isAuthenticated } = useAuth0();
  const { data: user } = useCurrentUser();
  const { logout } = useAuthorization();
  const { enabled, idleMins, warningMins } = useSessionTimeoutSettings();

  const [remainingMs, setRemainingMs] = useState<number>();
  const lastActivity = useRef(Date.now());
  const warningDeadline = useRef<number>();
  const idleMs = idleMins * MS_PER_MIN;
  const warningMs = warningMins * MS_PER_MIN;

  useEffect(() => {
    if (!isAuthenticated || !enabled) {
      warningDeadline.current = undefined;
      setRemainingMs(undefined);
      return;
    }

    lastActivity.current = Date.now();
    warningDeadline.current = undefined;

    const onActivity = () => {
      lastActivity.current = Date.now();
    };
    ACTIVITY_EVENTS.forEach(event => window.addEventListener(event, onActivity, { passive: true }));

    const timer = setInterval(() => {
      const now = Date.now();
      if (warningDeadline.current === undefined) {
        if (now - lastActivity.current < idleMs) return;
        warningDeadline.current = lastActivity.current + idleMs + warningMs;
      }

      const remaining = warningDeadline.current - now;
      if (remaining <= 0) {
        clearInterval(timer);
        logout('inactivity');
        return;
      }
      setRemainingMs(remaining);
    }, 1000);

    return () => {
      clearInterval(timer);
      ACTIVITY_EVENTS.forEach(event => window.removeEventListener(event, onActivity));
    };
  }, [isAuthenticated, enabled, idleMs, warningMs, logout]);

  const stay = () => {
    lastActivity.current = Date.now();
    warningDeadline.current = undefined;
    setRemainingMs(undefined);
  };

  return (
    <Dialog
      open={remainingMs !== undefined}
      title={lang.SessionTimeout.title}
      allowClose={false}
      confirmLabel={lang.SessionTimeout.stayLoggedIn}
      cancelLabel={lang.SessionTimeout.logOut}
      onConfirm={stay}
      onCancel={() => logout('user')}
      body={
        <Typography>
          {lang.SessionTimeout.description({
            email: user?.email ?? '',
            time: formatRemaining(remainingMs ?? 0),
          })}
        </Typography>
      }
    />
  );
};

export default SessionTimeoutWarning;
